import { API_BASE_URL } from '../config/api';
import { SessionResponse, ActiveSessionResponse } from '../types/auth';
import { Role, AssignablePermissions, CreateRolePayload, UpdateRolePayload } from '../types/roles';
import { User, UserListItem, CreateUserPayload } from '../types/users';
import { CustomField, CreateCustomFieldPayload, PatchCustomFieldPayload, DeleteCustomFieldPayload, UnlockCustomFieldPayload, SetHistoryPayload } from '../types/customFields';
import { EmployeeTag, CreateEmployeeTagPayload, PatchEmployeeTagPayload, SetTagHistoryPayload, UnlockEmployeeTagPayload } from '../types/employeeTags';
import { Shift, ShiftPayload } from '../types/shifts';
import { ShiftGroup, ShiftGroupPayload, PatchShiftGroupPayload } from '../types/shiftGroups';
import { ShiftDistribution, CreateShiftDistributionPayload, PatchShiftDistributionPayload } from '../types/shiftDistributions';
import { ApplicationSetting, FieldSetting, UIField, HistoryTrackingUnit, GlobalSettings, CalculatedValueDefinition } from '../types/applicationSettings';
import { CreateEmployeePayload, UpdateEmployeePayload, EmployeeRecord } from '../types/employees';
import { LeaveType, CreateLeaveTypePayload, UpdateLeaveTypePayload, LeavePolicy, CreateLeavePolicyPayload, UpdateLeavePolicyPayload, LeaveBalance, LedgerEntry, LedgerPage, PostConsumptionPayload, PostAdjustmentPayload, PublicHoliday, CreatePublicHolidayPayload, UpdatePublicHolidayPayload } from '../types/leave';
import { ParserProfile, CreateParserProfilePayload, SubmitImportPayload, TaskResponse, ManualTimekeepingEntry, TimekeepingErrorRow } from '../types/timekeeping';
import { ExecutePipelinePayload, BaseDataRecord, ProcessNode } from '../types/report';

export type { CreateRolePayload, UpdateRolePayload, CreateUserPayload, CreateCustomFieldPayload, PatchCustomFieldPayload, DeleteCustomFieldPayload, UnlockCustomFieldPayload, CreateEmployeeTagPayload, PatchEmployeeTagPayload };

export interface BootstrapData {
  roles?: Role[];
  permissions?: AssignablePermissions;
  users?: UserListItem[];
  customFields?: CustomField[];
  employeeTags?: EmployeeTag[];
  shifts?: Shift[];
  shiftGroups?: ShiftGroup[];
  shiftDistributions?: ShiftDistribution[];
  applicationSettings?: ApplicationSetting[];
  globalSettings?: GlobalSettings;
  uiFields?: UIField[];
  leaveTypes?: LeaveType[];
  leavePolicies?: LeavePolicy[];
  publicHolidays?: PublicHoliday[];
}

interface ApiSuccessResponse<T> {
  message: string;
  status: string;
  data: T;
  warnings?: string[];
  ignoredFields?: string[];
}

// API Service
class ApiService {
  private baseUrl: string;

  constructor() {
    if (!API_BASE_URL) {
      throw new Error('REACT_APP_API_URL environment variable is not set');
    }
    this.baseUrl = API_BASE_URL;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include', // Browser automatically sends HttpOnly cookies
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Request failed' }));

        // Permission-denied or session-expired 401/403 from API Gateway
        if (response.status === 401 || (response.status === 403 && error.message === 'Forbidden')) {
          // Clear session indicator cookie across host and domain variants
          document.cookie = 'has_session=; Path=/; Max-Age=0;';
          document.cookie = `has_session=; Path=/; Domain=${window.location.hostname}; Max-Age=0;`;
          const parts = window.location.hostname.split('.');
          if (parts.length > 2) {
            const parentDomain = parts.slice(1).join('.');
            document.cookie = `has_session=; Path=/; Domain=.${parentDomain}; Max-Age=0;`;
          }

          const isAuthRoute =
            window.location.pathname.startsWith('/login') ||
            window.location.pathname.startsWith('/not-authorized') ||
            window.location.pathname.startsWith('/auth/callback');

          if (!isAuthRoute) {
            window.location.href = '/login';
          }
          const httpError = new Error(error.message || 'Forbidden');
          (httpError as any).status = response.status;
          (httpError as any).data = error;
          (httpError as any).response = { status: response.status, data: error };
          throw httpError;
        }

        // Backend returns { error: "...", message: "..." }
        const errorMessage = error.error && error.message && error.error !== error.message
          ? `${error.error}: ${error.message}`
          : (error.error || error.message || 'Request failed');
        const httpError = new Error(errorMessage);
        (httpError as any).status = response.status;
        (httpError as any).data = error;
        (httpError as any).response = { status: response.status, data: error };
        // Preserve field-level validation errors for UI display
        if (error.jsonMessage) (httpError as any).jsonMessage = error.jsonMessage;
        throw httpError;
      }

      // Check if response has content before parsing JSON
      const text = await response.text();

      // If empty body, return empty object for type safety
      if (!text || text.trim() === '') {
        return {} as T;
      }

      return JSON.parse(text);
    } catch (error: any) {
      // Handle network errors
      if (error.name === 'TypeError' && (error.message.includes('fetch') || error.message.includes('Failed to fetch'))) {
        console.error('Network error details:', error);
        throw new Error(`Network error: Unable to reach API at ${url}. This could be a CORS issue. Check browser console for details.`);
      }
      throw error;
    }
  }

  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'GET',
    });
  }

  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
    });
  }

  async deleteWithBody<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: JSON.stringify(data),
    });
  }

  // Auth callback - exchanges code for session
  async authCallback(code: string, state: string): Promise<SessionResponse> {
    return this.post<SessionResponse>('/auth/callback', { code, state });
  }

  // Select unit - for users with multiple assignments
  async selectUnit(unitName: string): Promise<void> {
    await this.post('/auth/select-unit', { unit: unitName });
  }

  // Logout
  async logout(): Promise<void> {
    // Backend will clear the HttpOnly session cookie
    await this.post('/auth/logout');
  }

  // Get active session — returns user info if the session cookie is valid
  async getActiveSession(): Promise<ActiveSessionResponse> {
    return this.get<ActiveSessionResponse>('/auth/session');
  }

  // RBAC — Roles
  async listRoles(): Promise<ApiSuccessResponse<Role[]>> {
    return this.get<ApiSuccessResponse<Role[]>>('/system/roles');
  }

  async createRole(payload: CreateRolePayload): Promise<ApiSuccessResponse<Role>> {
    return this.post<ApiSuccessResponse<Role>>('/system/roles', payload);
  }

  async getRole(shortId: string): Promise<ApiSuccessResponse<Role>> {
    return this.get<ApiSuccessResponse<Role>>(`/system/role/${shortId}`);
  }

  async updateRole(shortId: string, payload: UpdateRolePayload): Promise<ApiSuccessResponse<Role>> {
    return this.put<ApiSuccessResponse<Role>>(`/system/role/${shortId}`, payload);
  }

  async deleteRole(shortId: string): Promise<void> {
    await this.delete(`/system/role/${shortId}`);
  }

  // RBAC — Assignable Permissions
  async getAssignablePermissions(): Promise<ApiSuccessResponse<AssignablePermissions>> {
    return this.get<ApiSuccessResponse<AssignablePermissions>>('/system/permissions');
  }

  // User Management
  async listUsers(): Promise<ApiSuccessResponse<UserListItem[]>> {
    return this.get<ApiSuccessResponse<UserListItem[]>>('/system/users');
  }

  async createUser(payload: CreateUserPayload): Promise<ApiSuccessResponse<User>> {
    return this.post<ApiSuccessResponse<User>>('/system/users', payload);
  }

  async getUser(username: string): Promise<ApiSuccessResponse<User>> {
    return this.get<ApiSuccessResponse<User>>(`/system/user/${encodeURIComponent(username)}`);
  }

  async deleteUser(username: string): Promise<void> {
    await this.delete(`/system/user/${encodeURIComponent(username)}`);
  }

  // User Role Assignment
  async getUserRoles(username: string): Promise<ApiSuccessResponse<string[]>> {
    return this.get<ApiSuccessResponse<string[]>>(`/system/user/${encodeURIComponent(username)}/roles`);
  }

  async assignUserRole(username: string, roleId: string): Promise<ApiSuccessResponse<null>> {
    return this.post<ApiSuccessResponse<null>>(`/system/user/${encodeURIComponent(username)}/roles`, { roleId });
  }

  async removeUserRole(username: string, roleId: string): Promise<ApiSuccessResponse<null>> {
    return this.delete<ApiSuccessResponse<null>>(`/system/user/${encodeURIComponent(username)}/roles/${encodeURIComponent(roleId)}`);
  }

  async getUserFieldDenials(username: string): Promise<{ deniedFields: string[] }> {
    return this.get<{ deniedFields: string[] }>(`/system/user/${encodeURIComponent(username)}/field-denials`);
  }

  async setUserFieldDenials(username: string, deniedFields: string[]): Promise<{ deniedFields: string[] }> {
    return this.put<{ deniedFields: string[] }>(`/system/user/${encodeURIComponent(username)}/field-denials`, { deniedFields });
  }

  // Custom Fields
  async listCustomFields(): Promise<ApiSuccessResponse<CustomField[]>> {
    return this.get<ApiSuccessResponse<CustomField[]>>('/custom-fields');
  }

  async createCustomField(payload: CreateCustomFieldPayload): Promise<ApiSuccessResponse<CustomField>> {
    return this.post<ApiSuccessResponse<CustomField>>('/custom-fields', payload);
  }

  async getCustomField(fieldName: string): Promise<ApiSuccessResponse<CustomField>> {
    return this.get<ApiSuccessResponse<CustomField>>(`/custom-fields/${encodeURIComponent(fieldName)}`);
  }

  async patchCustomField(fieldName: string, payload: PatchCustomFieldPayload): Promise<ApiSuccessResponse<CustomField>> {
    return this.patch<ApiSuccessResponse<CustomField>>(`/custom-fields/${encodeURIComponent(fieldName)}`, payload);
  }

  async deleteCustomField(fieldName: string, payload: DeleteCustomFieldPayload): Promise<ApiSuccessResponse<{ message: string }>> {
    return this.deleteWithBody<ApiSuccessResponse<{ message: string }>>(`/custom-fields/${encodeURIComponent(fieldName)}`, payload);
  }

  async unlockCustomField(fieldName: string, payload: UnlockCustomFieldPayload): Promise<ApiSuccessResponse<CustomField>> {
    return this.patch<ApiSuccessResponse<CustomField>>(`/custom-fields/${encodeURIComponent(fieldName)}/unlock`, payload);
  }

  async setCustomFieldHistory(fieldName: string, payload: SetHistoryPayload): Promise<ApiSuccessResponse<CustomField>> {
    return this.patch<ApiSuccessResponse<CustomField>>(`/custom-fields/${encodeURIComponent(fieldName)}/history`, payload);
  }

  // Employee Tags
  async listEmployeeTags(): Promise<ApiSuccessResponse<EmployeeTag[]>> {
    return this.get<ApiSuccessResponse<EmployeeTag[]>>('/employee-tags');
  }

  async createEmployeeTag(payload: CreateEmployeeTagPayload): Promise<ApiSuccessResponse<EmployeeTag>> {
    return this.post<ApiSuccessResponse<EmployeeTag>>('/employee-tags', payload);
  }

  async patchEmployeeTag(payload: PatchEmployeeTagPayload): Promise<ApiSuccessResponse<EmployeeTag>> {
    return this.patch<ApiSuccessResponse<EmployeeTag>>('/employee-tags', payload);
  }

  async getEmployeeTag(id: number): Promise<ApiSuccessResponse<EmployeeTag>> {
    return this.get<ApiSuccessResponse<EmployeeTag>>(`/employee-tags/${id}`);
  }

  async deleteEmployeeTag(id: number): Promise<ApiSuccessResponse<EmployeeTag>> {
    return this.delete<ApiSuccessResponse<EmployeeTag>>(`/employee-tags/${id}`);
  }

  async unlockEmployeeTag(id: number, payload: UnlockEmployeeTagPayload = {}): Promise<ApiSuccessResponse<EmployeeTag>> {
    return this.patch<ApiSuccessResponse<EmployeeTag>>(`/employee-tags/${id}/unlock`, payload);
  }

  async setEmployeeTagHistory(id: number, payload: SetTagHistoryPayload): Promise<ApiSuccessResponse<EmployeeTag>> {
    return this.patch<ApiSuccessResponse<EmployeeTag>>(`/employee-tags/${id}/history`, payload);
  }

  // Storage
  async getDownloadUrl(key: string): Promise<ApiSuccessResponse<{ url: string; key: string }>> {
    return this.get<ApiSuccessResponse<{ url: string; key: string }>>(`/storage/download?key=${encodeURIComponent(key)}`);
  }

  async getUploadUrl(key: string, contentType?: string): Promise<ApiSuccessResponse<{ url: string; key: string }>> {
    return this.post<ApiSuccessResponse<{ url: string; key: string }>>('/storage/upload', { key, contentType });
  }

  async deleteStorageObject(key: string): Promise<ApiSuccessResponse<{ key: string }>> {
    return this.delete<ApiSuccessResponse<{ key: string }>>(`/storage?key=${encodeURIComponent(key)}`);
  }

  // Shifts
  async listShifts(): Promise<ApiSuccessResponse<Shift[]>> {
    return this.get<ApiSuccessResponse<Shift[]>>('/shifts');
  }

  async createShift(payload: ShiftPayload): Promise<ApiSuccessResponse<Shift>> {
    return this.post<ApiSuccessResponse<Shift>>('/shifts', payload);
  }

  async updateShift(payload: ShiftPayload & { shiftId: string }): Promise<ApiSuccessResponse<Shift>> {
    return this.patch<ApiSuccessResponse<Shift>>('/shifts', payload);
  }

  async listShiftEditPoints(shiftId: string): Promise<ApiSuccessResponse<Shift[]>> {
    return this.get<ApiSuccessResponse<Shift[]>>(`/shifts/${encodeURIComponent(shiftId)}/edit-points`);
  }

  async deleteShift(shiftId: string): Promise<ApiSuccessResponse<Shift[]>> {
    return this.delete<ApiSuccessResponse<Shift[]>>(`/shifts/${encodeURIComponent(shiftId)}`);
  }

  async deleteShiftEditPoint(shiftId: string, date: string): Promise<ApiSuccessResponse<Shift>> {
    return this.delete<ApiSuccessResponse<Shift>>(
      `/shifts/${encodeURIComponent(shiftId)}/edit-points/${encodeURIComponent(date)}`
    );
  }

  async copyShiftEditPoint(shiftId: string, fromDate: string, toDate: string): Promise<ApiSuccessResponse<Shift>> {
    return this.post<ApiSuccessResponse<Shift>>(
      `/shifts/copy/${encodeURIComponent(shiftId)}/from/${encodeURIComponent(fromDate)}/to/${encodeURIComponent(toDate)}`,
      {}
    );
  }

  // Shift Groups
  async listShiftGroups(): Promise<ApiSuccessResponse<ShiftGroup[]>> {
    return this.get<ApiSuccessResponse<ShiftGroup[]>>('/shift-groups');
  }

  async createShiftGroup(payload: ShiftGroupPayload): Promise<ApiSuccessResponse<ShiftGroup>> {
    return this.post<ApiSuccessResponse<ShiftGroup>>('/shift-groups', payload);
  }

  async updateShiftGroup(payload: PatchShiftGroupPayload): Promise<ApiSuccessResponse<ShiftGroup>> {
    return this.patch<ApiSuccessResponse<ShiftGroup>>('/shift-groups', payload);
  }

  async listShiftGroupEditPoints(shiftGroupId: string): Promise<ApiSuccessResponse<ShiftGroup[]>> {
    return this.get<ApiSuccessResponse<ShiftGroup[]>>(`/shift-groups/${encodeURIComponent(shiftGroupId)}`);
  }

  async getShiftGroupEditPoint(shiftGroupId: string, date: string): Promise<ApiSuccessResponse<ShiftGroup>> {
    return this.get<ApiSuccessResponse<ShiftGroup>>(
      `/shift-groups/${encodeURIComponent(shiftGroupId)}/edit-points/${encodeURIComponent(date)}`
    );
  }

  async getEffectiveShiftGroupEditPoint(shiftGroupId: string, date: string): Promise<ApiSuccessResponse<ShiftGroup>> {
    return this.get<ApiSuccessResponse<ShiftGroup>>(
      `/shift-groups/${encodeURIComponent(shiftGroupId)}/edit-points/${encodeURIComponent(date)}/effective`
    );
  }

  async createShiftGroupEditPoint(shiftGroupId: string, payload: ShiftGroupPayload): Promise<ApiSuccessResponse<ShiftGroup>> {
    return this.post<ApiSuccessResponse<ShiftGroup>>(
      `/shift-groups/${encodeURIComponent(shiftGroupId)}/edit-points`,
      payload
    );
  }

  async deleteShiftGroup(shiftGroupId: string): Promise<ApiSuccessResponse<ShiftGroup[]>> {
    return this.delete<ApiSuccessResponse<ShiftGroup[]>>(`/shift-groups/${encodeURIComponent(shiftGroupId)}`);
  }

  async deleteShiftGroupEditPoint(shiftGroupId: string, date: string): Promise<ApiSuccessResponse<ShiftGroup>> {
    return this.delete<ApiSuccessResponse<ShiftGroup>>(
      `/shift-groups/${encodeURIComponent(shiftGroupId)}/edit-points/${encodeURIComponent(date)}`
    );
  }

  // Shift Distributions
  async listShiftDistributions(): Promise<ApiSuccessResponse<ShiftDistribution[]>> {
    return this.get<ApiSuccessResponse<ShiftDistribution[]>>('/shift-distributions');
  }

  async createShiftDistribution(payload: CreateShiftDistributionPayload): Promise<ApiSuccessResponse<ShiftDistribution>> {
    return this.post<ApiSuccessResponse<ShiftDistribution>>('/shift-distributions', payload);
  }

  async getShiftDistribution(date: string): Promise<ApiSuccessResponse<ShiftDistribution>> {
    return this.get<ApiSuccessResponse<ShiftDistribution>>(`/shift-distributions/${encodeURIComponent(date)}`);
  }

  async updateShiftDistribution(payload: PatchShiftDistributionPayload): Promise<ApiSuccessResponse<ShiftDistribution>> {
    return this.patch<ApiSuccessResponse<ShiftDistribution>>('/shift-distributions', payload);
  }

  async deleteShiftDistribution(date: string): Promise<ApiSuccessResponse<ShiftDistribution>> {
    return this.delete<ApiSuccessResponse<ShiftDistribution>>(`/shift-distributions/${encodeURIComponent(date)}`);
  }

  // Application Settings
  async listApplicationSettings(): Promise<ApiSuccessResponse<ApplicationSetting[]>> {
    return this.get<ApiSuccessResponse<ApplicationSetting[]>>('/application-settings');
  }

  async createApplicationSetting(payload: { settingName: string; active: boolean; settingJsonString?: string }): Promise<ApiSuccessResponse<ApplicationSetting>> {
    return this.post<ApiSuccessResponse<ApplicationSetting>>('/application-settings', payload);
  }

  async getApplicationSetting(settingId: string): Promise<ApiSuccessResponse<ApplicationSetting>> {
    return this.get<ApiSuccessResponse<ApplicationSetting>>(`/application-settings/${encodeURIComponent(settingId)}`);
  }

  async updateApplicationSetting(settingId: string, payload: { settingName?: string; active?: boolean; settingJsonString?: string }): Promise<ApiSuccessResponse<ApplicationSetting>> {
    return this.patch<ApiSuccessResponse<ApplicationSetting>>(`/application-settings/${encodeURIComponent(settingId)}`, payload);
  }

  async deleteApplicationSetting(settingId: string): Promise<ApiSuccessResponse<{ message: string }>> {
    return this.delete<ApiSuccessResponse<{ message: string }>>(`/application-settings/${encodeURIComponent(settingId)}`);
  }

  async getFieldList(settingId: string): Promise<ApiSuccessResponse<FieldSetting[]>> {
    return this.get<ApiSuccessResponse<FieldSetting[]>>(`/application-settings/${encodeURIComponent(settingId)}/field-list`);
  }

  async saveFieldList(settingId: string, fieldList: FieldSetting[]): Promise<ApiSuccessResponse<FieldSetting[]>> {
    return this.patch<ApiSuccessResponse<FieldSetting[]>>(`/application-settings/${encodeURIComponent(settingId)}/field-list`, { fieldSettingList: fieldList });
  }

  async getUIFields(): Promise<ApiSuccessResponse<UIField[]>> {
    return this.get<ApiSuccessResponse<UIField[]>>('/application-settings/ui-fields');
  }

  async getGlobalApplicationSettings(): Promise<ApiSuccessResponse<GlobalSettings>> {
    return this.get<ApiSuccessResponse<GlobalSettings>>('/application-settings/global');
  }

  async updateGlobalApplicationSettings(payload: {
    historyTrackingUnit?: HistoryTrackingUnit;
    timekeepingTimezone?: string;
    deactivateClosestEntries?: boolean;
    deactivateClosestEntriesThresholdSecond?: number;
    timekeepingErrorDetectionModeStrict?: boolean;
    serverSideCaching?: boolean;
  }): Promise<ApiSuccessResponse<GlobalSettings>> {
    return this.patch<ApiSuccessResponse<GlobalSettings>>('/application-settings/global', payload);
  }

  // Manual Timekeeping Entry
  async queryManualTimekeepingEntries(payload: { date: string; employees: { employeeInternalId: string; biometricId: string }[] }): Promise<ApiSuccessResponse<{ data: Record<string, ManualTimekeepingEntry[]> }>> {
    return this.post('/timekeeping/manual-entry/query', payload);
  }

  async batchManualTimekeepingEntries(payload: { date: string; data: Record<string, { biometricId: string; add: string[]; remove: string[]; reactivate?: string[] }> }): Promise<ApiSuccessResponse<{ processedEmployees: number }>> {
    return this.post('/timekeeping/manual-entry', payload);
  }

  // Timekeeping Error Correction
  async queryTimekeepingErrors(payload: {
    date: string;
    shiftId?: string;
    shiftGroupId?: string;
    showRunning: boolean;
    employees: { employeeInternalId: string; biometricId: string; employeeName: string }[];
    page: number;
    pageSize: number;
  }): Promise<ApiSuccessResponse<{ totalCount: number; data: TimekeepingErrorRow[] }>> {
    return this.post('/timekeeping/errors/query', payload);
  }

  async saveTimekeepingError(payload: {
    employeeInternalId: string;
    biometricId: string;
    add?: string;
    remove?: string;
    reactivate?: string;
  }): Promise<ApiSuccessResponse<{ success: boolean; message?: string }>> {
    return this.post('/timekeeping/errors/save', payload);
  }

  // Calculated Value Definitions
  async listCalculatedValueDefinitions(): Promise<ApiSuccessResponse<CalculatedValueDefinition[]>> {
    return this.get<ApiSuccessResponse<CalculatedValueDefinition[]>>('/calculated-value-definitions');
  }

  async createCalculatedValueDefinition(payload: { label: string; triggerFieldName: string; code: string }): Promise<ApiSuccessResponse<CalculatedValueDefinition>> {
    return this.post<ApiSuccessResponse<CalculatedValueDefinition>>('/calculated-value-definitions', payload);
  }

  async updateCalculatedValueDefinition(id: string, payload: { label?: string; triggerFieldName?: string; code?: string }): Promise<ApiSuccessResponse<CalculatedValueDefinition>> {
    return this.patch<ApiSuccessResponse<CalculatedValueDefinition>>(`/calculated-value-definitions/${encodeURIComponent(id)}`, payload);
  }

  async deleteCalculatedValueDefinition(id: string): Promise<ApiSuccessResponse<{ message: string }>> {
    return this.delete<ApiSuccessResponse<{ message: string }>>(`/calculated-value-definitions/${encodeURIComponent(id)}`);
  }

  // Bootstrap
  async getBootstrap(): Promise<ApiSuccessResponse<BootstrapData>> {
    return this.get<ApiSuccessResponse<BootstrapData>>('/app-data');
  }

  // Employees
  async listEmployees(): Promise<ApiSuccessResponse<EmployeeRecord[]>> {
    return this.get<ApiSuccessResponse<EmployeeRecord[]>>('/employees');
  }

  async createEmployee(payload: CreateEmployeePayload): Promise<{ data: { employeeUUID: string; employeeInternalId: string }; warnings?: string[]; ignoredFields?: string[] }> {
    return this.post<{ data: { employeeUUID: string; employeeInternalId: string } }>('/employees', payload);
  }

  async getEmployee(uuid: string): Promise<ApiSuccessResponse<EmployeeRecord>> {
    return this.get<ApiSuccessResponse<EmployeeRecord>>(`/employees/${encodeURIComponent(uuid)}`);
  }

  async getEmployeeByInternalId(id: string): Promise<ApiSuccessResponse<EmployeeRecord>> {
    return this.get<ApiSuccessResponse<EmployeeRecord>>(`/employees/by-employeeid/${encodeURIComponent(id)}`);
  }

  async updateEmployee(uuid: string, payload: UpdateEmployeePayload): Promise<ApiSuccessResponse<EmployeeRecord>> {
    return this.put<ApiSuccessResponse<EmployeeRecord>>(`/employees/${encodeURIComponent(uuid)}`, payload);
  }

  async getEmployeePhotoUploadUrl(uuid: string): Promise<ApiSuccessResponse<{ url: string; key: string }>> {
    return this.post<ApiSuccessResponse<{ url: string; key: string }>>(`/employees/${encodeURIComponent(uuid)}/photo`);
  }

  async getEmployeeSignatureUploadUrl(uuid: string): Promise<ApiSuccessResponse<{ url: string; key: string }>> {
    return this.post<ApiSuccessResponse<{ url: string; key: string }>>(`/employees/${encodeURIComponent(uuid)}/signature`);
  }

  async getOutdatedEmployeeUUIDs(timestamp: number): Promise<ApiSuccessResponse<string[]>> {
    return this.get<ApiSuccessResponse<string[]>>(`/employees/get-outdated-employee-uuid/${timestamp}`);
  }

  async getEmployeeList(uuids: string[]): Promise<ApiSuccessResponse<EmployeeRecord[]>> {
    return this.post<ApiSuccessResponse<EmployeeRecord[]>>('/employees/get-list', { employeeUUIDList: uuids });
  }

  async getFrontendCacheId(uuid: string | null): Promise<ApiSuccessResponse<{ uuid: string }>> {
    return this.get<ApiSuccessResponse<{ uuid: string }>>(
      `/get-frontend-cache-id/${encodeURIComponent(uuid ?? 'null')}`
    );
  }

  async deleteFrontendCacheByUUID(uuid: string): Promise<ApiSuccessResponse<{ message: string }>> {
    return this.delete<ApiSuccessResponse<{ message: string }>>(
      `/maintenance/clear-frontend-cache/${encodeURIComponent(uuid)}`
    );
  }

  async deleteAllFrontendCaches(): Promise<ApiSuccessResponse<{ message: string; count: number }>> {
    return this.delete<ApiSuccessResponse<{ message: string; count: number }>>(
      '/maintenance/clear-frontend-cache'
    );
  }

  async recreateEmployeeIndices(): Promise<ApiSuccessResponse<{ message: string }>> {
    return this.post<ApiSuccessResponse<{ message: string }>>(
      '/maintenance/recreate-employee-indices',
      {}
    );
  }

  async getEmployeeImportConfig(): Promise<ApiSuccessResponse<{ s3Uri: string }>> {
    return this.get<ApiSuccessResponse<{ s3Uri: string }>>('/maintenance/employee-import/config');
  }

  async startEmployeeImport(): Promise<ApiSuccessResponse<{ taskId: string; groupName: string }>> {
    return this.post<ApiSuccessResponse<{ taskId: string; groupName: string }>>(
      '/maintenance/employee-import',
      {}
    );
  }

  async importEmployeeTags(payload: { data: any[] }): Promise<ApiSuccessResponse<{ message: string; imported: number; matched: number }>> {
    return this.post<ApiSuccessResponse<{ message: string; imported: number; matched: number }>>(
      '/maintenance/employee-tags/import',
      payload
    );
  }


  // Leave Types
  async listLeaveTypes(): Promise<ApiSuccessResponse<LeaveType[]>> {
    return this.get<ApiSuccessResponse<LeaveType[]>>('/leave-types');
  }

  async createLeaveType(payload: CreateLeaveTypePayload): Promise<ApiSuccessResponse<LeaveType>> {
    return this.post<ApiSuccessResponse<LeaveType>>('/leave-types', payload);
  }

  async getLeaveType(id: string): Promise<ApiSuccessResponse<LeaveType>> {
    return this.get<ApiSuccessResponse<LeaveType>>(`/leave-types/${encodeURIComponent(id)}`);
  }

  async updateLeaveType(id: string, payload: UpdateLeaveTypePayload): Promise<ApiSuccessResponse<LeaveType>> {
    return this.patch<ApiSuccessResponse<LeaveType>>(`/leave-types/${encodeURIComponent(id)}`, payload);
  }

  async deleteLeaveType(id: string): Promise<ApiSuccessResponse<{ message: string }>> {
    return this.delete<ApiSuccessResponse<{ message: string }>>(`/leave-types/${encodeURIComponent(id)}`);
  }

  // Leave Policies
  async listLeavePolicies(leaveTypeId: string): Promise<ApiSuccessResponse<LeavePolicy[]>> {
    return this.get<ApiSuccessResponse<LeavePolicy[]>>(`/leave-policies?leaveTypeId=${encodeURIComponent(leaveTypeId)}`);
  }

  async createLeavePolicy(payload: CreateLeavePolicyPayload): Promise<ApiSuccessResponse<LeavePolicy>> {
    return this.post<ApiSuccessResponse<LeavePolicy>>('/leave-policies', payload);
  }

  async getLeavePolicy(leaveTypeId: string, effectiveDate: string): Promise<ApiSuccessResponse<LeavePolicy>> {
    return this.get<ApiSuccessResponse<LeavePolicy>>(`/leave-policy?leaveTypeId=${encodeURIComponent(leaveTypeId)}&effectiveDate=${encodeURIComponent(effectiveDate)}`);
  }

  async updateLeavePolicy(payload: UpdateLeavePolicyPayload): Promise<ApiSuccessResponse<LeavePolicy>> {
    return this.patch<ApiSuccessResponse<LeavePolicy>>('/leave-policy', payload);
  }

  // Leave Balance
  async getLeaveBalance(employeeId: string, leaveTypeId: string): Promise<ApiSuccessResponse<LeaveBalance>> {
    return this.get<ApiSuccessResponse<LeaveBalance>>(`/leave-balance?employeeId=${encodeURIComponent(employeeId)}&leaveTypeId=${encodeURIComponent(leaveTypeId)}`);
  }

  // Leave Transactions
  async listLeaveTransactions(employeeId: string, leaveTypeId: string): Promise<ApiSuccessResponse<LedgerPage>> {
    return this.get<ApiSuccessResponse<LedgerPage>>(`/leave-transactions?employeeId=${encodeURIComponent(employeeId)}&leaveTypeId=${encodeURIComponent(leaveTypeId)}`);
  }

  async postLeaveConsumption(payload: PostConsumptionPayload): Promise<ApiSuccessResponse<LedgerEntry>> {
    return this.post<ApiSuccessResponse<LedgerEntry>>('/leave-transactions', payload);
  }

  async postLeaveAdjustment(payload: PostAdjustmentPayload): Promise<ApiSuccessResponse<LedgerEntry>> {
    return this.post<ApiSuccessResponse<LedgerEntry>>('/leave-transactions', payload);
  }

  // Public Holidays
  async listPublicHolidays(year?: string): Promise<ApiSuccessResponse<PublicHoliday[]>> {
    const query = year ? `?year=${encodeURIComponent(year)}` : '';
    return this.get<ApiSuccessResponse<PublicHoliday[]>>(`/public-holidays${query}`);
  }

  async createPublicHoliday(payload: CreatePublicHolidayPayload): Promise<ApiSuccessResponse<PublicHoliday>> {
    return this.post<ApiSuccessResponse<PublicHoliday>>('/public-holidays', payload);
  }

  async getPublicHoliday(date: string): Promise<ApiSuccessResponse<PublicHoliday>> {
    return this.get<ApiSuccessResponse<PublicHoliday>>(`/public-holidays/${encodeURIComponent(date)}`);
  }

  async updatePublicHoliday(date: string, payload: UpdatePublicHolidayPayload): Promise<ApiSuccessResponse<PublicHoliday>> {
    return this.patch<ApiSuccessResponse<PublicHoliday>>(`/public-holidays/${encodeURIComponent(date)}`, payload);
  }

  async deletePublicHoliday(date: string): Promise<ApiSuccessResponse<{ message: string }>> {
    return this.delete<ApiSuccessResponse<{ message: string }>>(`/public-holidays/${encodeURIComponent(date)}`);
  }

  // Timekeeping — Parser Profiles
  async listParserProfiles(): Promise<ApiSuccessResponse<ParserProfile[]>> {
    return this.get<ApiSuccessResponse<ParserProfile[]>>('/timekeeping/parser-profiles');
  }

  async getParserProfile(id: string): Promise<ApiSuccessResponse<ParserProfile>> {
    return this.get<ApiSuccessResponse<ParserProfile>>(`/timekeeping/parser-profiles/${encodeURIComponent(id)}`);
  }

  async createParserProfile(payload: CreateParserProfilePayload): Promise<ApiSuccessResponse<ParserProfile>> {
    return this.post<ApiSuccessResponse<ParserProfile>>('/timekeeping/parser-profiles', payload);
  }

  async updateParserProfile(id: string, payload: Partial<CreateParserProfilePayload>): Promise<ApiSuccessResponse<ParserProfile>> {
    return this.put<ApiSuccessResponse<ParserProfile>>(`/timekeeping/parser-profiles/${encodeURIComponent(id)}`, payload);
  }

  async deleteParserProfile(id: string): Promise<ApiSuccessResponse<{ message: string }>> {
    return this.delete<ApiSuccessResponse<{ message: string }>>(`/timekeeping/parser-profiles/${encodeURIComponent(id)}`);
  }

  // Timekeeping — Import
  async getTimekeepingQueueStatus(): Promise<ApiSuccessResponse<{ queueName: string; queueEnabled: boolean; message: string }>> {
    return this.get<ApiSuccessResponse<{ queueName: string; queueEnabled: boolean; message: string }>>('/timekeeping/import');
  }

  async submitTimekeepingImport(payload: SubmitImportPayload): Promise<ApiSuccessResponse<{ taskId: string; groupName: string }>> {
    return this.post<ApiSuccessResponse<{ taskId: string; groupName: string }>>('/timekeeping/import', payload);
  }

  async getTask(groupName: string, taskId: string): Promise<ApiSuccessResponse<TaskResponse>> {
    return this.get<ApiSuccessResponse<TaskResponse>>(`/tasks/${encodeURIComponent(groupName)}/${encodeURIComponent(taskId)}`);
  }

  async listTasks(groupName: string): Promise<ApiSuccessResponse<TaskResponse[]>> {
    return this.get<ApiSuccessResponse<TaskResponse[]>>(`/tasks/${encodeURIComponent(groupName)}`);
  }

  async cancelTask(groupName: string, taskId: string): Promise<ApiSuccessResponse<{ message: string }>> {
    return this.patch<ApiSuccessResponse<{ message: string }>>(`/tasks/${encodeURIComponent(groupName)}/${encodeURIComponent(taskId)}/cancel`);
  }

  async getActiveEmployeeSnapshots(): Promise<ApiSuccessResponse<string[]>> {
    return this.get<ApiSuccessResponse<string[]>>('/maintenance/active-employees/initiated');
  }

  async generateActiveEmployeeBaselineSnapshot(month: string): Promise<ApiSuccessResponse<{ message: string }>> {
    return this.post<ApiSuccessResponse<{ message: string }>>('/maintenance/active-employees/generate/baseline', { month });
  }

  async generateActiveEmployeeIncrementalSnapshot(month: string): Promise<ApiSuccessResponse<{ message: string }>> {
    return this.post<ApiSuccessResponse<{ message: string }>>('/maintenance/active-employees/generate/incremental', { month });
  }

  // Report — Data Source Pipeline
  async getReportPipelineTree(): Promise<ApiSuccessResponse<ProcessNode[]>> {
    return this.get<ApiSuccessResponse<ProcessNode[]>>('/reports/data-source/pipeline');
  }

  async saveReportPipelineTree(nodes: ProcessNode[]): Promise<ApiSuccessResponse<{ message: string; nodes: ProcessNode[] }>> {
    return this.put<ApiSuccessResponse<{ message: string; nodes: ProcessNode[] }>>('/reports/data-source/pipeline', nodes);
  }

  async executeReportDataSource(payload: ExecutePipelinePayload): Promise<ApiSuccessResponse<BaseDataRecord[]>> {
    const start = new Date(`${payload.fromDate}T00:00:00Z`).getTime();
    const end = new Date(`${payload.toDate}T00:00:00Z`).getTime();
    const days = Math.round((end - start) / 86400000) + 1;

    if (days > 40) {
      throw new Error(`Date range (${days} days) exceeds the maximum allowed limit of 40 days.`);
    }
    if (days < 1) {
      throw new Error('Invalid date range: toDate must be on or after fromDate.');
    }

    let endpoint = '/reports/data-source/execute';
    if (days >= 21) {
      endpoint = '/reports/data-source/execute-heavy';
    } else if (days >= 11) {
      endpoint = '/reports/data-source/execute-medium';
    }

    const res = await this.post<ApiSuccessResponse<BaseDataRecord[]> & { dataUrl?: string }>(endpoint, payload);
    if (res.dataUrl) {
      const fetchRes = await fetch(res.dataUrl);
      if (!fetchRes.ok) {
        throw new Error(`Failed to download report data from storage: ${fetchRes.statusText}`);
      }

      const buffer = await fetchRes.arrayBuffer();
      const bytes = new Uint8Array(buffer);

      // Detect gzip format by magic bytes (0x1F, 0x8B)
      const isGzip = bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b;
      const DecompStream = (window as any).DecompressionStream;

      if (isGzip && typeof DecompStream !== 'undefined') {
        const ds = new DecompStream('gzip');
        const writer = ds.writable.getWriter();
        writer.write(buffer);
        writer.close();
        const decompressedText = await new Response(ds.readable).text();
        return JSON.parse(decompressedText) as ApiSuccessResponse<BaseDataRecord[]>;
      }

      // Plain JSON (already decompressed natively by browser via Content-Encoding or uncompressed)
      const text = new TextDecoder('utf-8').decode(buffer);
      return JSON.parse(text) as ApiSuccessResponse<BaseDataRecord[]>;
    }
    return res;
  }

}

export const apiService = new ApiService();

